import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { apiErrorMessage } from '@/lib/apiError';
import { formatMoney } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  submitted: 'bg-yellow-100 text-yellow-700',
  under_review: 'bg-blue-100 text-blue-700',
  needs_info: 'bg-orange-100 text-orange-700',
  approved: 'bg-green-100 text-green-700',
  paid: 'bg-emerald-100 text-emerald-700',
  denied: 'bg-red-100 text-red-700',
};

// Lo que se puede decidir desde cada estado. El backend lo controla igual
// (CLAIM_TRANSITIONS); aca solo evita mostrar botones que van a fallar.
const ACCIONES = {
  submitted: ['under_review', 'needs_info', 'approved', 'denied'],
  under_review: ['needs_info', 'approved', 'denied'],
  needs_info: ['under_review', 'approved', 'denied'],
  approved: ['paid', 'denied'],
};

const ETIQUETA = {
  under_review: 'Start review',
  needs_info: 'Ask for information',
  approved: 'Approve',
  denied: 'Deny',
  paid: 'Record payment',
};

const dinero = (n) => (n === null || n === undefined ? '—' : formatMoney(n));
const fecha = (s) => (s ? new Date(s).toLocaleDateString() : '—');

/**
 * Un reclamo de SelleTrans en la pantalla del admin, con su decision.
 *
 * La pantalla mostraba estos reclamos con los campos de los reclamos viejos
 * (monto y fecha vacios) y sin botones, porque los viejos empiezan en
 * "pending" y estos en "submitted": no se podian revisar.
 */
export default function SelleTransClaimReview({ claim, token, onDone }) {
  const [notas, setNotas] = useState('');
  const [monto, setMonto] = useState(claim.payable_estimate ?? '');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const decidir = async (status) => {
    setBusy(status);
    setError('');
    try {
      const body = { status, reviewer_notes: notas.trim() || null };
      if (status === 'approved') body.amount_awarded = parseFloat(monto);
      const r = await fetch(`${API}/api/insurance/selletrans/claims/${claim.claim_number}/review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(apiErrorMessage(j, 'The decision was not saved'));
      setNotas('');
      await onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const acciones = ACCIONES[claim.status] || [];

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4" data-testid={`st-claim-${claim.claim_number}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs ${COLOR[claim.status] || 'bg-gray-100 text-gray-700'}`}>{claim.status}</span>
            <span className="font-medium capitalize">{String(claim.claim_type || '').replaceAll('_', ' ')} claim</span>
            <span className="font-mono text-xs text-slate-500">{claim.claim_number} · coverage {claim.verification_number}</span>
            {claim.late_notice && (
              <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700" data-testid={`st-late-${claim.claim_number}`}>
                late notice · {claim.notice_days} days after discovery
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">{claim.description}</p>
          {claim.user_info && (
            <p className="text-xs text-slate-500 mt-1">Client: {claim.user_info.company_name} ({claim.user_info.email})</p>
          )}
          <p className="text-xs text-slate-400 mt-1">
            Incident {fecha(claim.incident_date)} · discovered {fecha(claim.discovered_date || claim.incident_date)} ·
            {' '}filed {fecha(claim.filed_at)} · records due {fecha(claim.records_due_at)}
          </p>
          {claim.reviewer_notes && (
            <p className="text-xs text-slate-500 mt-1">Last note: {claim.reviewer_notes}</p>
          )}
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0">
          <p className="text-lg font-bold text-slate-900 dark:text-white">{dinero(claim.amount_claimed)}</p>
          <p>Coverage pays up to {dinero(claim.max_payout)}</p>
          <p>Deductible {dinero(claim.deductible)} · estimate {dinero(claim.payable_estimate)}</p>
          {claim.amount_awarded !== null && claim.amount_awarded !== undefined && <p className="text-green-700">Awarded {dinero(claim.amount_awarded)}</p>}
          {claim.status === 'paid' && <p className="text-emerald-700">Paid {dinero(claim.amount_paid)} on {fecha(claim.paid_at)}</p>}
        </div>
      </div>

      {acciones.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
          <div className="flex gap-2 flex-wrap items-end">
            <label className="text-xs text-slate-500 flex-1 min-w-[220px]">
              {claim.status === 'approved' ? 'Payment reference (required to record the payment)' : 'Notes (required to deny or ask for information; the merchant sees them)'}
              <input className="w-full h-9 px-2 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
                value={notas} onChange={(e) => setNotas(e.target.value)} data-testid={`st-notes-${claim.claim_number}`} />
            </label>
            {acciones.includes('approved') && (
              <label className="text-xs text-slate-500 w-32">
                Amount to approve
                <input type="number" min="0" max={claim.max_payout ?? undefined}
                  className="w-full h-9 px-2 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
                  value={monto} onChange={(e) => setMonto(e.target.value)} data-testid={`st-amount-${claim.claim_number}`} />
              </label>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {acciones.map((a) => (
              <Button key={a} size="sm" variant={a === 'denied' ? 'destructive' : a === 'approved' || a === 'paid' ? 'default' : 'outline'}
                onClick={() => decidir(a)} disabled={!!busy} data-testid={`st-${a}-${claim.claim_number}`}>
                {busy === a ? <Loader2 className="w-4 h-4 animate-spin" /> : ETIQUETA[a]}
              </Button>
            ))}
          </div>
          {claim.status === 'approved' && (
            <p className="text-[11px] text-slate-500">
              Recording the payment does not send money. Pay the merchant first, then record it here with the reference.
            </p>
          )}
          {error && <p className="text-xs text-rose-600" data-testid={`st-error-${claim.claim_number}`}>{error}</p>}
        </div>
      )}
    </div>
  );
}
